import {
  Box,
  Button,
  CascaderField,
  Col,
  DatePickerField,
  Divider,
  Form,
  LabeledSelect,
  RadioGroupField,
  Row,
  SelectField,
  Text,
  TextAreaInputField,
  TextInputField,
  theme,
} from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { TagSelectFieldForEdit } from '@/features/tag/components';
import { tagType } from '@/features/tag/models';
import { UserSelectField } from '@/features/tenant-manager/components';
import {
  useHierarchyLabels,
  useOrganizationOptionsRecursive,
} from '@/features/tenant-manager/hooks';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import { useWorkflowTemplates } from '@/features/workflow/hooks/template';
import { useCreateWorkflow } from '@/features/workflow/hooks/workflow';
import { useMessage } from '@/providers';
import { isRS } from '@/utils';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { type FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  type WorkflowFromTemplateFormSchema,
  WorkflowStageTypeLabelModifier,
  WorkflowTypeLabelModifier,
  workflowStageType,
  workflowType,
} from '../../../models/workflow';

type FormValues = z.infer<typeof WorkflowFromTemplateFormSchema>;
type InitialFormValues = Partial<FormValues>;

export const WORKFLOW_FORM_ID = 'create-from-template';

const workflowStageTypeOptions = workflowStageType.options.map((value) => ({
  value,
  label: WorkflowStageTypeLabelModifier[value],
}));

const workflowTypeOptions = workflowType.options.map((value) => ({
  value,
  label: WorkflowTypeLabelModifier[value],
}));

export const WorkflowCreateFromTemplateForm: FC = () => {
  const { t } = useTranslation();

  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'create-from-template',
    t('unreachable-code'),
  );

  const { openMessage } = useMessage();
  const me = useMe();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>();
  const templates = useWorkflowTemplates();

  const templateOptions = templates.data?.map((d) => ({
    label: d.name,
    value: d.id,
  }));

  const { mutateAsync: createWorkflowAsync } = useCreateWorkflow({
    projectId:
      siderConfig.target.key === 'project' ? siderConfig.target.id : undefined,
  });
  const organizationOptions = useOrganizationOptionsRecursive();

  const { data: hierarchyLabelOptions } = useHierarchyLabels({
    params: { enabledOnly: true },
    select: (data) =>
      data.map((label) => ({
        value: label.id,
        label: label.name,
      })),
  });

  const initialValues: InitialFormValues = useMemo(() => {
    return {
      ...(isRS() && {
        title: siderConfig.target.name,
        workflowType: 'strict',
      }),
      assigneeIds: me.data ? [me.data.id] : [],
      ...(templates.data?.find((d) => d.id === selectedTemplateId)
        ?.data as InitialFormValues),
    };
  }, [selectedTemplateId, templates.data, siderConfig.target.name, me.data]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty, isSubmitting, isSubmitSuccessful },
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const { token } = theme.useToken();

  const {
    fields: stageFields,
    append: appendStage,
    insert: insertStage,
    remove: removeStage,
  } = useFieldArray<InitialFormValues>({
    control,
    name: 'stages',
  });

  const onSubmit = async (values: FormValues) => {
    if (!me.data) {
      return;
    }
    await createWorkflowAsync({
      assigneeIds: values.assigneeIds,
      content: values.content,
      deadline: values.deadline,
      remark: values.remark,
      tagIds: values.tagIds,
      // NOTE: sharedOrganizationIds is a nested array, so we need to extract the last organization id of each array
      sharedOrganizationIds: values.sharedOrganizationRecursiveIds.map(
        (organizationRecursiveIds) => organizationRecursiveIds.at(-1) ?? '',
      ),
      title: values.title,
      stages: values.stages,
      workflowType: values.workflowType,
      key: siderConfig.target.key,
      objectId: siderConfig.target.id,
    });

    openMessage('success', t('message-create-completed'));

    stackSiderView.pop();
  };

  useEffect(() => {
    reset((prev) => ({
      ...prev,
      ...initialValues,
    }));
  }, [initialValues, reset]);

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      id={WORKFLOW_FORM_ID}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <Box marginBottom={16}>
        <LabeledSelect
          label={t('workflow.template-name')}
          value={selectedTemplateId}
          onChange={(value) => {
            setSelectedTemplateId(value);
            stackSiderView.updateLastItem({
              config: {
                ...siderConfig,
                formValues: {
                  templateSelected: !!value,
                },
              },
            });
          }}
          options={templateOptions}
          fullWidth
          vertical
        />
      </Box>

      <Divider />
      {selectedTemplateId && (
        <>
          <TextInputField
            name="title"
            autocompleteKey="workflow.title"
            label={t('workflow.name')}
            control={control}
            errors={errors}
            required={!isRS()}
            disabled={isRS()}
          />
          <TagSelectFieldForEdit
            name="tagIds"
            control={control}
            errors={errors}
            type={tagType.Values.workflow}
            startYear="current"
            endYear="current"
          />
          <UserSelectField
            mode="multiple"
            name="assigneeIds"
            label={t('assignee')}
            control={control}
            errors={errors}
            useNotInOption
            required
            organizationFilter="modal"
          />
          {!isRS() && (
            <CascaderField
              name="sharedOrganizationRecursiveIds"
              label={t('viewable-organization')}
              control={control}
              errors={errors}
              options={organizationOptions.data}
              multiple
              enableIntermediate
              allowClear={false}
              loading={organizationOptions.isLoading}
            />
          )}
          <DatePickerField
            name="deadline"
            label={t('deadline')}
            picker="date"
            disabledDate={(current) => {
              return current < dayjs().subtract(1, 'day').endOf('day');
            }}
            control={control}
            errors={errors}
          />
          {!isRS() && (
            <TextAreaInputField
              name="content"
              label={t('description')}
              control={control}
              errors={errors}
              rows={5}
              maxLength={1500}
            />
          )}
          <TextAreaInputField
            name="remark"
            label={t('remark')}
            control={control}
            errors={errors}
            rows={5}
            maxLength={300}
          />

          <Box marginBottom={8}>
            <Text variant="h5" weight="bold" text={t('workflow')} />
          </Box>
          <RadioGroupField
            name="workflowType"
            label={t('workflow-type')}
            control={control}
            options={workflowTypeOptions}
            errors={errors}
            disabled={isRS()}
          />
          <Form.Item>
            <Row gutter={[0, 8]}>
              {stageFields.map((item, idx) => (
                <Fragment key={item.id}>
                  {idx !== 0 && (
                    <Col span={24}>
                      <Button
                        type="dashed"
                        onClick={() => {
                          insertStage(idx, {
                            title: null as any,
                            stageType: null as any,
                            approverIds: [],
                            labelId: undefined,
                          });
                        }}
                        block
                        icon={<PlusOutlined aria-label="" />}
                      >
                        {t('add')}
                      </Button>
                    </Col>
                  )}
                  <Col
                    style={{
                      padding: 16,
                      backgroundColor: token.colorBgLayout,
                    }}
                    span={24}
                  >
                    <Row justify="space-between">
                      <Col>
                        <Text
                          variant="h5"
                          weight="bold"
                          text={t('stage-number', { stageNumber: idx + 1 })}
                        />
                      </Col>
                      <Col>
                        <Button
                          type="link"
                          onClick={() => {
                            removeStage(idx);
                          }}
                        >
                          {t('delete')}
                        </Button>
                      </Col>
                    </Row>
                    <TextInputField
                      name={`stages.${idx}.title`}
                      autocompleteKey="stage.title"
                      label={t('stage-title')}
                      placeholder={t('placeholder-stage-title')}
                      control={control}
                      errors={errors}
                    />

                    <SelectField
                      name={`stages.${idx}.labelId`}
                      label={t('label')}
                      control={control}
                      options={hierarchyLabelOptions}
                      useNotInOption
                      errors={errors}
                      labelRender={({ value }) => {
                        const matchHierarchyLabel = hierarchyLabelOptions?.find(
                          (label) => label.value === value,
                        );
                        return matchHierarchyLabel?.label ?? value;
                      }}
                    />

                    <RadioGroupField
                      name={`stages.${idx}.stageType`}
                      label={t('stage-type')}
                      control={control}
                      options={workflowStageTypeOptions}
                      errors={errors}
                      required
                    />

                    <UserSelectField
                      mode="multiple"
                      name={`stages.${idx}.approverIds`}
                      label={t('approver')}
                      control={control}
                      errors={errors}
                      organizationFilter="modal"
                      useNotInOption
                    />
                  </Col>
                </Fragment>
              ))}
              <Col span={24}>
                <Button
                  type="dashed"
                  onClick={() => {
                    appendStage({
                      title: null as any,
                      stageType: null as any,
                      approverIds: [],
                      labelId: undefined,
                    });
                  }}
                  block
                  icon={<PlusOutlined aria-label="" />}
                >
                  {t('add')}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </>
      )}
    </Form>
  );
};
